// Angular modules.
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// Third-party modules.
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
// Zenklub interfaces.
import { IContact } from 'app/interfaces/contact.interface';
import { IUserPreferences } from 'app/interfaces/userpreferences.interface';
// Zenklub services.
import { ContactsService } from 'app/services/contacts.service';
import { CurrencyService } from 'app/services/currency.service';
import { DateTimeService } from 'app/services/datetime.service';
import { EventsService } from 'app/services/events.service';
import { GlobalService } from 'app/services/global.service';
import { UserService } from 'app/services/user.service';
import { enterAnimation } from 'app/utils/animations.util';

import { IChatUser } from '../../interfaces/chatuser.interface';
import { ChatService } from '../../services/chat.service';

@Component({
	selector: 'app-clientsdetails',
	templateUrl: './clients-details.component.html',
	styleUrls: ['./clients-details.component.scss'],
	animations: [enterAnimation],
})
export class ClientsDetailsComponent implements OnInit {
	// Outputs.
	@Output() editClient: EventEmitter<number> = new EventEmitter<number>();
	@Output() newAppointment: EventEmitter<any> = new EventEmitter<any>();
	@Output() showAllClients: EventEmitter<any> = new EventEmitter<any>();
	@Output() showEventDetails: EventEmitter<any> = new EventEmitter<any>();

	// User data.
	userId: number;
	userData: IContact;
	userPref: IUserPreferences;
	// Client event list stuff
	isContact: boolean;
	clientId: string;

	// Status.
	isLoading = false;
	isMobile = false;
	isRedirecting = false;
	showDetails = true;
	isSendingInvite = false;
	isInviteSent = false;

	// Constructor.
	constructor(
		private chat: ChatService,
		protected contact: ContactsService,
		protected currency: CurrencyService,
		protected dateTime: DateTimeService,
		protected events: EventsService,
		protected notifications: ZkNotificationService,
		protected global: GlobalService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected translate: TranslateService,
		protected user: UserService
	) {}

	// On Init.
	public ngOnInit(): void {
		// Loader.
		this.isLoading = true;
		// Verify if is a mobile device.
		this.isMobile = this.global.isMobileDevice();

		// Subscribe to router events.
		// Redirect if needed.
		this.router.events.subscribe((event) => {
			if (!this.isRedirecting) {
				const ev: any = event;
				const url: string = ev.url;
				if (url === '/clients') {
					this.isRedirecting = true;
					this.showAllClients.emit();
				}
			}
		});

		// Get user preferences.
		this.userPref = this.user.getUserLocalPreferences();
		// Get user ID.
		this.route.firstChild.params.subscribe((params) => {
			if (params['id']) {
				// Store user ID.
				this.userId = params['id'];
				// Get user data.
				this.contact.getContact(this.userId).subscribe((res) => {
					// Store user data.
					this.userData = res;
					this.userData.statusText = this.contact.getStatusText(
						this.userData.status
					);
					this.userData.notes =
						this.userData.notes == null ? '' : this.userData.notes;
					// Get contact events.
					this.setEventsToSearch();
				});
			} else {
				// Loader.
				this.isLoading = false;
			}
		});
	}

	// Get contact events.
	public setEventsToSearch(): void {
		// if its still a contact with no account in zenklub
		if (!this.userData.refUserId) {
			this.isContact = true;
			this.clientId = this.userId.toString();
		} else {
			this.isContact = false;
			this.clientId = this.userData.refUserId;
		}
		// Loader.
		this.isLoading = false;
	}

	// Go to clients list.
	public goBack(): void {
		this.router.navigate(['/contacts']);
	}

	// Edit client.
	public edit(id: number): void {
		if (id) {
			this.editClient.emit(id);
		}
	}

	// delete the contact.
	public deleteContact(e: any, contactId: string): void {
		e.stopPropagation();

		// we need to alert and confirm?
		if (contactId) {
			if (confirm(this.translate.instant('MESSAGES.CONFIRMCLIENTREMOVE'))) {
				this.isLoading = true;
				this.contact.deleteContactById(contactId).subscribe(
					() => {
						this.isLoading = false;
						this.notifications.showSuccess(
							this.translate.instant('MESSAGES.CLIENTREMOVED')
						);
						this.router.navigate(['clients']);
					},
					(e) => {
						// 404 means contact not found
						if (e.status === 404) {
							this.notifications.showError(
								e.error.messageI18n,
								this.translate.instant('MESSAGES.ERRORINVALIDCLIENT')
							);
						} else {
							// generic message
							this.notifications.showError(
								e.error.messageI18n,
								this.translate.instant('MESSAGES.ERRORCLIENT')
							);
						}
						this.isLoading = false;
					}
				);
			}
		}
	}

	// Create an appointment.
	public createAppointment(e, userId: string, isContact = false): void {
		e.stopPropagation();

		if (userId) {
			if (this.isMobile) {
				this.router.navigate(['/appointments/add/' + userId], {
					queryParams: { returnTo: 'clients' },
				});
			} else {
				this.newAppointment.emit({ userId, isContact });
			}
		}
	}

	// Show details of an event.
	public showEvent(eventId: string): void {
		if (eventId) {
			this.showEventDetails.emit(eventId);
		}
	}

	// Show/hide details section.
	public toggleShowDetails(): void {
		this.showDetails = !this.showDetails;
	}

	// Send invite to user.
	public inviteUser(userId: number): void {
		this.isSendingInvite = true;
		this.isInviteSent = false;

		// Messages.
		const succMsg: string = this.isMobile ? 'INVITED' : 'INVITATIONSENT';
		const msgSuccess: string = this.translate.instant(
			'PAGES.CLIENTS.MESSAGES.' + succMsg
		);
		const msgError: string = this.translate.instant(
			'PAGES.CLIENTS.MESSAGES.INVITATIONNOTSENT'
		);
		//
		this.contact.inviteUser(userId).subscribe(
			() => {
				this.isSendingInvite = false;
				this.isInviteSent = true;
				this.notifications.showSuccess(msgSuccess);
			},
			(e) => {
				this.isSendingInvite = true;
				this.isInviteSent = false;
				this.notifications.showError(e.error.messageI18n, msgError);
			}
		);
	}

	async showChat(userData: IContact) {
		const chatUser: IChatUser = {
			id: userData?.refUserId,
			email: userData?.email,
			photoUrl: userData?.photoUrl,
			username: userData?.displayName,
			role: this.chat.role,
		};
		await this.chat.startConversation(chatUser, 'old-contact-page');
	}
}
